import React, { useState, useEffect, useRef, useCallback } from 'react';
import { realtimeClient } from '../lib/directus';

export default function ChatPage() {
  const [messages, setMessages] = useState([]);
  const [loggedIn, setLoggedIn] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesRef = useRef([]);

  // Keep ref in sync for closure access
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const addMessage = useCallback((msg) => {
    setMessages((prev) => {
      // Prevent duplicates by id
      if (msg.id && prev.some((m) => m.id === msg.id)) return prev;
      return [...prev, msg];
    });
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    const email = e.target.elements.email.value;
    const password = e.target.elements.password.value;

    setConnecting(true);
    setError(null);

    try {
      const cleanup = realtimeClient.onWebSocket('message', function (data) {
        if (data.type === 'auth' && data.status === 'ok') {
          setLoggedIn(true);
          setConnecting(false);

          // Read existing messages
          realtimeClient.sendMessage({
            type: 'items',
            collection: 'messages',
            action: 'read',
            query: {
              limit: 50,
              sort: '-date_created',
              fields: ['*', 'user_created.first_name'],
            },
          });

          // Subscribe to new messages
          subscribeToMessages();
        }

        if (data.type === 'auth' && data.status === 'error') {
          setError('Authentication failed. Check your credentials.');
          setConnecting(false);
        }

        if (data.type === 'items') {
          const items = data.data || [];
          // Historical messages come in reverse order, reverse them
          const sorted = [...items].reverse();
          sorted.forEach((item) => addMessage(item));
        }
      });

      await realtimeClient.connect();
      await realtimeClient.login(email, password);

      return cleanup;
    } catch (err) {
      console.error('Connection error:', err);
      setError('Failed to connect to Directus. Make sure the server is running.');
      setConnecting(false);
    }
  };

  async function subscribeToMessages() {
    try {
      const { subscription } = await realtimeClient.subscribe('messages', {
        event: 'create',
        query: {
          fields: ['*', 'user_created.first_name'],
        },
      });

      for await (const message of subscription) {
        if (message.type === 'subscription' && message.event === 'create') {
          const newMsgs = message.data || [];
          newMsgs.forEach((msg) => addMessage(msg));
        }
      }
    } catch (err) {
      console.error('Subscription error:', err);
    }
  }

  const handleSendMessage = (e) => {
    e.preventDefault();
    const text = e.target.elements.text.value.trim();
    if (!text) return;

    realtimeClient.sendMessage({
      type: 'items',
      collection: 'messages',
      action: 'create',
      data: { text },
    });

    e.target.reset();
  };

  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center px-5">
        <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-8 w-full max-w-sm shadow-lg">
          <h1 className="text-xl font-bold text-neutral-50 mb-2">Chat Login</h1>
          <p className="text-sm text-neutral-400 mb-6">
            Sign in with your Directus account to join the chat.
          </p>

          {error && (
            <div className="bg-red-500/10 border border-red-500/25 text-red-400 px-3 py-2 rounded-md text-sm mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-neutral-400 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                required
                className="w-full px-3 py-2 bg-neutral-950 border border-neutral-700 rounded-md text-neutral-100 text-sm focus:border-primary-400 focus:outline-none"
              />
            </div>
            <div className="mb-6">
              <label htmlFor="password" className="block text-sm font-medium text-neutral-400 mb-1">
                Password
              </label>
              <input
                type="password"
                id="password"
                required
                className="w-full px-3 py-2 bg-neutral-950 border border-neutral-700 rounded-md text-neutral-100 text-sm focus:border-primary-400 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={connecting}
              className="w-full py-2 bg-primary-400 text-neutral-950 font-semibold rounded-md hover:bg-primary-300 transition-colors disabled:opacity-50"
            >
              {connecting ? 'Connecting...' : 'Join Chat'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 flex flex-col">
      {/* Header */}
      <div className="bg-neutral-900 border-b border-neutral-800 px-5 py-3">
        <h1 className="text-lg font-bold text-neutral-50">Chat</h1>
        <p className="text-xs text-neutral-500">Real-time messaging powered by Directus</p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
        {messages.length === 0 && (
          <p className="text-neutral-500 text-center mt-10">No messages yet. Say something!</p>
        )}
        {messages.map((msg, idx) => (
          <div key={msg.id || idx} className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-primary-400/20 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-primary-400">
                {msg.user_created?.first_name?.[0] || '?'}
              </span>
            </div>
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-semibold text-neutral-100">
                  {msg.user_created?.first_name || 'Unknown'}
                </span>
                {msg.date_created && (
                  <span className="text-xs text-neutral-600">
                    {new Date(msg.date_created).toLocaleTimeString()}
                  </span>
                )}
              </div>
              <p className="text-sm text-neutral-300 mt-0.5">{msg.text}</p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSendMessage} className="bg-neutral-900 border-t border-neutral-800 px-5 py-3 flex gap-3">
        <input
          type="text"
          id="text"
          placeholder="Type a message..."
          autoComplete="off"
          className="flex-1 px-3 py-2 bg-neutral-950 border border-neutral-700 rounded-md text-neutral-100 text-sm focus:border-primary-400 focus:outline-none"
        />
        <button
          type="submit"
          className="px-5 py-2 bg-primary-400 text-neutral-950 font-semibold rounded-md hover:bg-primary-300 transition-colors text-sm"
        >
          Send
        </button>
      </form>
    </div>
  );
}
